import React, { useCallback, useMemo, useState } from 'react';
import styles from './AddCompositionPanel.module.css';
import { PlusIcon } from 'features/compositions/assets/icons/icons';
import AddCompositionUnit from './AddCompositionUnit';
import { AddCompositionContextProvider, useAddCompositionContext } from 'features/compositions/context/AddCompositionContext';
import useTheme from 'config/useTheme';
import dataTypesConfig from 'features/compositions/constants/dataTypes.json';
import AddObject from '../composition-record/AddObject';
import AddCompositionAuthorization from './AddCompositionAuthorization';
import { validateCompositionName } from 'utils/validations';
import AddCompositionProcess from './AddCompositionProcess';
import { AIIcon } from 'assets/icons';
import AICompositionDialog from './AICompositionDialog';
import { useIsPluginActive } from 'features/plugins';

// Van komponente: izvedeno je iz statičkog JSON-a, pa nema šta da se računa
// pri svakom iscrtavanju — a kao nov niz svaki put obarao bi i `useCallback`
// koji ga drži.
const allowedIndexTypes = dataTypesConfig.indexTypes.map(t => t.value);

const AddCompositionPanelInner = () => {
    const { dark } = useTheme();
    const [view, setView] = useState('records');
    const [aiDialogOpen, setAIDialogOpen] = useState(false);
    const [aiSessionId, setAiSessionId] = useState(null);
    const { step, setStep, tables, addNewTable, allCompositionsList, setStructs, customTypes, setCustomTypes, setOriginalTablesState, setTablesFromCompositions } = useAddCompositionContext();
    const [recordTypes, setRecordTypes] = useState(dataTypesConfig.dataTypes);

    const isAIArchitectPluginActive = useIsPluginActive(12); // AI Database Architect plugin ID

    const isValidTable = useCallback((table) => {
        const customTypeNames = customTypes.map(ct => ct.name);
        const allValidIndexTypes = [...allowedIndexTypes, ...customTypeNames];

        const hasValidIndex = table.fields?.some(field =>
            field.is_index === true && allValidIndexTypes.includes(field.type)
        );

        const hasValidName = validateCompositionName(table.name, allCompositionsList);
        return hasValidIndex && hasValidName;
    }, [customTypes, allCompositionsList]);

    const allTablesValid = useMemo(() => {
        return tables.every(isValidTable);
    }, [tables, isValidTable]);

    // Recursive function to convert custom types to objects
    const convertCustomTypesToObjects = (fields) => {
        return fields.map(field => {
            if (customTypes.some(ct => ct.name === field.type)) {
                const customType = customTypes.find(ct => ct.name === field.type);
                return {
                    name: field.name,
                    type: field.type,
                    is_index: field.is_index || false,
                    ai: field.ai || false,
                    default_value: field.default_value || '',
                    description: field.description || '',
                    is_object: true,
                    is_array: field.is_array || false,
                    object: {
                        name: customType.name,
                        description: customType.description || '',
                        fields: convertCustomTypesToObjects(customType.fields)
                    }
                };
            }
            return {
                name: field.name,
                type: field.type,
                is_index: field.is_index || false,
                ai: field.ai || false,
                default_value: field.default_value || '',
                description: field.description || '',
                is_object: false,
                is_array: field.is_array || false,
                ...(field.type === 'time_point' && {
                    is_auto_created: field.is_auto_created || false,
                    is_auto_updated: field.is_auto_updated || false
                })
            };
        });
    };

    const onCreate = () => {
        // Save the original state before switching the indexes
        setOriginalTablesState([...tables]);

        // Convert custom types to objects and prepare compositions
        const compositions = tables.map(table => {
            const indexes = table.fields.filter(field => field.is_index === true);
            const nonIndexFields = table.fields.filter(field => !field.is_index);

            // Convert custom types to objects recursively
            const processedFields = convertCustomTypesToObjects(nonIndexFields);

            // Process indexes
            const processedIndexes = indexes.map(index => ({
                name: index.name,
                type: index.type,
                ai: index.ai || false,
                default_value: index.default_value || '',
                description: index.description || '',
                ...(index.type === 'time_point' && {
                    is_auto_created: index.is_auto_created || false,
                    is_auto_updated: index.is_auto_updated || false
                })
            }));

            return {
                name: table.name,
                permission: table.permission || 'active',
                description: table.description || '',
                fields: processedFields,
                indexes: processedIndexes
            };
        });

        setStructs(compositions);
        setStep(2);
    }

    return (
        <div className={`${styles.add_composition_panel} ${dark ? styles.dark : ''}`}>
            {step === 1 &&
                <>
                    <div className={styles.top_line}>
                        <span className={styles.title}>Add new composition</span>
                        <div className={styles.top_line_right}>
                            <button onClick={() => setView('add-object')} className={styles.new_composition_btn}>Custom type <PlusIcon /></button>
                            <button
                                onClick={() => { addNewTable(); if (view !== 'records') { setView('records') }; }}
                                disabled={tables.length >= 10}
                                className={styles.new_composition_btn}>
                                Composition <PlusIcon />
                            </button>
                            <button disabled={!allTablesValid} onClick={() => onCreate()} className={styles.create_all_btn}>Create</button>
                        </div>
                    </div>
                    {view === 'records' &&
                        <div className={styles.tables}>
                            {tables?.map((t, index) => (
                                <AddCompositionUnit data={t} index={index} key={index} recordTypes={recordTypes} />
                            ))}
                        </div>
                    }

                    {view === 'add-object' &&
                        <AddObject
                            dataTypes={recordTypes}
                            setDataTypes={setRecordTypes}
                            setView={setView}
                            setCustomTypes={setCustomTypes}
                            customTypes={customTypes}
                        />
                    }
                    {isAIArchitectPluginActive && (
                        <button
                            type="button"
                            className={styles.ai_fab}
                            onClick={() => setAIDialogOpen(true)}
                            title="AI Database Architect (Beta)"
                            aria-label="Open AI Database Architect (Beta)"
                        >
                            <span className={styles.ai_fab_badge}>Beta</span>
                            <AIIcon color="#fff" />
                        </button>
                    )}
                    <AICompositionDialog
                        open={aiDialogOpen}
                        onClose={() => setAIDialogOpen(false)}
                        sessionId={aiSessionId}
                        onSessionIdChange={setAiSessionId}
                        onConfirm={(structure) => {
                            setTablesFromCompositions(structure);
                            if (view !== 'records') setView('records');
                        }}
                    />
                </>
            }
            {step === 2 && <AddCompositionAuthorization />}
            {step === 3 && <AddCompositionProcess />}
        </div>
    )
}

const AddCompositionPanel = () => (
    <AddCompositionContextProvider>
        <AddCompositionPanelInner />
    </AddCompositionContextProvider>
)

export default AddCompositionPanel;